import {DocumentNode, MutationHookOptions, OperationVariables, TypedDocumentNode, useMutation} from "@apollo/client";
import classNames from "classnames";
import isFunction from "lodash/isFunction";
import {ReactNode, useState} from "react";
import {OverlayTrigger, Tooltip} from "react-bootstrap";

import Button from "@/components/button/Button.tsx";
import {ButtonSize, ButtonVariant} from "@/components/button/Button.types.ts";
import {DeleteActionDialog} from "@/components/deleteActionDialog/DeleteActionDialog.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {FormDrawerActionDialog} from "@/components/form/overlay/FormDrawerActionDialog.tsx";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {TableCrownButton} from "@/components/tableCrownButton/TableCrownButton.tsx";
import {GraphQLResponse, ResponseStatus} from "@/lib/types/common.ts";

interface ButtonWithConfirmationDialogProps {
  dialogTitle: string;
  dialogElement: React.ReactNode;
  dialogSize?: "sm" | "lg" | "xl";
  tooltip?: ReactNode;
  className?: string;
  mutation_variables?: OperationVariables;
  mutation_hook_options?: MutationHookOptions;
  mutation_GQL: DocumentNode | TypedDocumentNode;
  children: React.ReactNode;
  postMutationAction?: (response: GraphQLResponse<unknown>) => void;
  dialogType?: "popup" | "drawer" | "delete";
  variant?: "tableRow" | "tableCrown";
  buttonVariant?: ButtonVariant;
  buttonSize?: ButtonSize;
}

// if you disable the button the tooltip will not work react-bootstrap/react-bootstrap#1588
export const ButtonWithMutationAndConfirmationDialog = <T extends GraphQLResponse<unknown>>({
  dialogTitle,
  dialogElement,
  dialogSize,
  tooltip = <></>,
  className,
  mutation_variables,
  mutation_hook_options,
  mutation_GQL,
  children,
  postMutationAction,
  dialogType = "popup",
  variant = "tableRow",
  buttonVariant,
  buttonSize,
  ...rest
}: ButtonWithConfirmationDialogProps) => {
  const [show, setShow] = useState(false);
  const [mutation, {loading, data, reset}] = useMutation<T>(mutation_GQL, mutation_hook_options);

  const handleConfirmation = async () => {
    const response = await mutation({
      variables: mutation_variables,
    });

    if (response?.data?.response?.status === ResponseStatus.SUCCESS) {
      setShow(false);
      reset();
      if (postMutationAction && isFunction(postMutationAction)) {
        postMutationAction(response.data as GraphQLResponse<unknown>);
      }
    }
  };

  const errors =
    data?.response?.status === ResponseStatus.ERROR
      ? Object.values(data?.response?.message as Record<string, unknown>)
          .filter(Array.isArray)
          .flat()
      : [];

  let actionDialog = (
    <ActionDialog
      show={show}
      size={dialogSize}
      loading={loading}
      title={dialogTitle}
      onConfirm={handleConfirmation}
      onCancel={() => {
        setShow(false);
        reset();
      }}
    >
      {dialogElement}
      <FormErrorAlerts errors={errors}></FormErrorAlerts>
    </ActionDialog>
  );
  if (dialogType === "delete") {
    actionDialog = (
      <DeleteActionDialog
        show={show}
        loading={loading}
        title={dialogTitle}
        onConfirm={handleConfirmation}
        onCancel={() => {
          setShow(false);
          reset();
        }}
      >
        {dialogElement}
      </DeleteActionDialog>
    );
  }
  // if you want to use a drawer instead of a popup
  if (dialogType === "drawer") {
    actionDialog = (
      <FormDrawerActionDialog
        show={show}
        title={dialogTitle}
        onCancel={() => setShow(false)}
        onConfirm={handleConfirmation}
      >
        {dialogElement}
      </FormDrawerActionDialog>
    );
  }

  return (
    <>
      <>
        {buttonVariant ? (
          <Button
            variant={buttonVariant}
            size={buttonSize}
            className={className}
            onClick={() => setShow(true)}
            {...rest}
          >
            {children}
          </Button>
        ) : variant === "tableRow" ? (
          <OverlayTrigger
            placement="top"
            overlay={typeof tooltip === "string" ? <Tooltip>{tooltip}</Tooltip> : <>{tooltip}</>}
          >
            <button
              className={classNames("btn px-2 p-0 d-flex align-items-center", className)}
              onClick={() => setShow(true)}
              {...rest}
            >
              {children}
            </button>
          </OverlayTrigger>
        ) : (
          <TableCrownButton onClick={() => setShow(true)} tooltip={tooltip} {...rest}>
            {children}
          </TableCrownButton>
        )}
      </>
      {actionDialog}
    </>
  );
};
